/**
 * PremiumActionContext - Intent-driven paywall system
 * 
 * Architecture: Paywall-After-Intent Model
 * 
 * Flow:
 * 1. User performs high-intent action (e.g., "Start Subscription Hunt")
 * 2. Action is queued in pendingPremiumAction state
 * 3. Subscription status is checked
 * 4. If not subscribed → Present paywall as continuation step
 * 5. If subscribed successfully → Auto-resume intended action
 * 6. If user cancels → Return to previous context gracefully
 * 
 * Key Principles:
 * - Preserve emotional momentum
 * - No premature paywall exposure
 * - Seamless post-subscription continuation
 * - Thread-safe action execution
 * - Support for multiple premium features
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from './SubscriptionContext';

// Premium action types
export const PREMIUM_ACTIONS = {
  PLAID_CONNECT: 'plaid_connect',
  SUBSCRIPTION_HUNT: 'subscription_hunt',
  ADVANCED_AI: 'advanced_ai',
  FULL_HISTORY: 'full_history',
  TAX_CALCULATOR: 'tax_calculator',
  CREDIT_INSIGHTS: 'credit_insights'
};

// Action metadata for UI
export const ACTION_METADATA = {
  [PREMIUM_ACTIONS.PLAID_CONNECT]: {
    title: 'Bank Connection',
    description: 'Securely connect your bank to auto-import transactions and find hidden subscriptions.',
    icon: '🏦',
    feature: 'bank_sync',
    successMessage: 'Let\'s connect your bank now!'
  },
  [PREMIUM_ACTIONS.SUBSCRIPTION_HUNT]: {
    title: 'Subscription Hunter',
    description: 'Automatically detect and track all your recurring subscriptions with bank sync.',
    icon: '🎯',
    feature: 'bank_sync',
    successMessage: 'Starting the subscription hunt!'
  },
  [PREMIUM_ACTIONS.ADVANCED_AI]: {
    title: 'AI Financial Insights',
    description: 'Get personalized financial advice and spending analysis from Guapo AI.',
    icon: '🤖',
    feature: 'advanced_ai',
    successMessage: 'Unlocking AI insights!'
  },
  [PREMIUM_ACTIONS.FULL_HISTORY]: {
    title: 'Full History Access',
    description: 'View your complete transaction history beyond the current month.',
    icon: '📊',
    feature: 'full_history',
    successMessage: 'Accessing your full history!'
  },
  [PREMIUM_ACTIONS.TAX_CALCULATOR]: {
    title: '1099 Tax Calculator',
    description: 'Track self-employment income and estimate taxes automatically.',
    icon: '📋',
    feature: 'tax_calculator',
    successMessage: 'Opening tax calculator!'
  },
  [PREMIUM_ACTIONS.CREDIT_INSIGHTS]: {
    title: 'Credit Building Tips',
    description: 'Get personalized tips to improve your credit score.',
    icon: '💳',
    feature: 'credit_insights',
    successMessage: 'Loading credit insights!'
  }
};

const PremiumActionContext = createContext(null);

export function PremiumActionProvider({ children }) {
  const navigate = useNavigate();
  const { canAccessFeature, refreshSubscription, isPremium, loading: subLoading } = useSubscription();
  
  // Pending action queue - persists user intent
  const [pendingAction, setPendingAction] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  
  // Callbacks for action completion
  const actionCallbackRef = useRef(null);
  const actionContextRef = useRef(null);
  
  // Thread-safe execution lock
  const executionLockRef = useRef(false);

  /**
   * Queue a premium action with intent preservation
   * @param {string} actionType - One of PREMIUM_ACTIONS
   * @param {Function} onSuccess - Callback when action can proceed
   * @param {Object} context - Additional context for the action
   */
  const queuePremiumAction = useCallback((actionType, onSuccess, context = {}) => {
    // Prevent duplicate queuing
    if (executionLockRef.current) {
      console.log('[PremiumAction] Execution in progress, ignoring duplicate');
      return;
    }

    const metadata = ACTION_METADATA[actionType];
    if (!metadata) {
      console.error('[PremiumAction] Unknown action type:', actionType);
      return;
    }

    // Store intent
    setPendingAction({
      type: actionType,
      metadata,
      timestamp: Date.now(),
      context
    });
    
    // Store callbacks
    actionCallbackRef.current = onSuccess;
    actionContextRef.current = context;

    // Check if user can access this feature
    if (canAccessFeature(metadata.feature)) {
      // User has access - execute immediately
      executeAction(actionType, onSuccess, context);
    } else {
      // User needs subscription - show paywall
      setShowPaywall(true);
    }
  }, [canAccessFeature]);

  /**
   * Execute the pending action
   */
  const executeAction = useCallback(async (actionType, callback, context) => {
    if (executionLockRef.current) {
      console.log('[PremiumAction] Already executing, skipping');
      return;
    }

    executionLockRef.current = true;
    setIsExecuting(true);

    try {
      if (callback && typeof callback === 'function') {
        await callback(context);
      }
    } catch (error) {
      console.error('[PremiumAction] Execution error:', error);
    } finally {
      executionLockRef.current = false;
      setIsExecuting(false);
      clearPendingAction();
    }
  }, []);

  /**
   * Handle successful subscription
   * Automatically resumes the pending action
   */
  const handleSubscriptionSuccess = useCallback(async () => {
    console.log('[PremiumAction] Subscription success, resuming action');
    
    // Close paywall
    setShowPaywall(false);
    
    // Refresh subscription status
    await refreshSubscription();
    
    // Resume pending action
    if (pendingAction && actionCallbackRef.current) {
      // Small delay for UI transition
      setTimeout(() => {
        executeAction(
          pendingAction.type, 
          actionCallbackRef.current, 
          actionContextRef.current
        );
      }, 300);
    }
  }, [pendingAction, refreshSubscription, executeAction]);

  /**
   * Handle paywall cancellation
   * Returns user to previous context gracefully
   */
  const handlePaywallCancel = useCallback(() => {
    console.log('[PremiumAction] Paywall cancelled');
    setShowPaywall(false);
    clearPendingAction();
  }, []);

  /**
   * Clear pending action state
   */
  const clearPendingAction = useCallback(() => {
    setPendingAction(null);
    actionCallbackRef.current = null;
    actionContextRef.current = null;
  }, []);

  /**
   * Navigate to pricing page while preserving intent
   */
  const navigateToPricing = useCallback(() => {
    // Store pending action in session for post-subscription resumption
    if (pendingAction) {
      sessionStorage.setItem('pendingPremiumAction', JSON.stringify({
        type: pendingAction.type,
        context: pendingAction.context,
        timestamp: Date.now()
      }));
    }
    setShowPaywall(false);
    navigate('/app/pricing');
  }, [pendingAction, navigate]);

  /**
   * Check for and resume pending action after returning from pricing
   */
  const checkPendingResumption = useCallback(async () => {
    const stored = sessionStorage.getItem('pendingPremiumAction');
    if (!stored) return false;

    try {
      const action = JSON.parse(stored);
      
      // Check if action is still valid (within 30 minutes)
      if (Date.now() - action.timestamp > 30 * 60 * 1000) {
        sessionStorage.removeItem('pendingPremiumAction');
        return false;
      }

      // Refresh subscription status
      await refreshSubscription();

      // Check if user now has access
      const metadata = ACTION_METADATA[action.type];
      if (metadata && canAccessFeature(metadata.feature)) {
        sessionStorage.removeItem('pendingPremiumAction');
        return {
          type: action.type,
          context: action.context,
          metadata
        };
      }
    } catch (e) {
      console.error('[PremiumAction] Error checking resumption:', e);
    }
    
    return false;
  }, [refreshSubscription, canAccessFeature]);

  const value = {
    // State
    pendingAction,
    showPaywall,
    isExecuting,
    
    // Actions
    queuePremiumAction,
    handleSubscriptionSuccess,
    handlePaywallCancel,
    navigateToPricing,
    clearPendingAction,
    checkPendingResumption,
    
    // Metadata
    ACTION_METADATA,
    PREMIUM_ACTIONS
  };

  return (
    <PremiumActionContext.Provider value={value}>
      {children}
    </PremiumActionContext.Provider>
  );
}

export function usePremiumAction() {
  const context = useContext(PremiumActionContext);
  if (!context) {
    throw new Error('usePremiumAction must be used within a PremiumActionProvider');
  }
  return context;
}

export default PremiumActionContext;
